import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { get, isNumber, reduce } from 'lodash';
import { createModel, RematchRootState } from '@rematch/core';

import {
  AccountBalances,
  ChannelPartnerResultSet,
  Country,
  CountryResultSet,
  CustomerPurchaseOrderResponseSummary,
  Customization,
  CustomizationResultSet,
  Organization,
  OrganizationProductInventorySet,
  OrganizationUser,
  ProductResultSet,
  RefundResponse,
  SearchDescription,
  ShipmentExactLimitedResponse,
  ShipmentResultSet,
} from '@libraries/api/gen-api';
import { DashboardResponse, PurchaseOrder } from '@libraries/api/gen-api-admin';
import {
  ChannelPartnerProductInventoryResponse,
  type GrantsResponse,
  PartnerPurchaseOrderResponseSet,
  PartnerPurchaseOrderResponseSummary,
  ResellerViewResponseItem,
} from '@libraries/api/gen-api-hidden';
import {
  SAMLConfigurationPatchRequest,
  SAMLConfigurationResponse,
  SAMLConfigurationUpdateRequest,
} from '@libraries/api/gen-api-hidden-sso';

import constructGetAbortController from './getAbortController';

import type {
  BreadCrumbsParamsType,
  FeatureToggles,
  InventoryAllocationByProductIdResponse,
  SearchParams,
  SearchState,
  SortDirection,
  SubscriptionInventoryAllocationResponse,
} from '../../types/types';

import { DEFAULT_PAGE, getDefaultSearchParams, ShipmentStatus } from '../helpers';

import { api } from '../api';
import { isNotEmpty } from '../utils';
import type { RootModel } from '.';

interface ConsoleSearchStates {
  shipments: SearchState<ShipmentExactLimitedResponse>;
  incompleteShipments: SearchState<ShipmentExactLimitedResponse>;
  purchaseOrders: SearchState<CustomerPurchaseOrderResponseSummary>;
  customers: SearchState<ResellerViewResponseItem>;
  resellers: SearchState<DistributorViewResponse>;
  distributorPurchaseOrders: SearchState<PartnerPurchaseOrderResponseSummary>;
  resellerPurchaseOrders: SearchState<PartnerPurchaseOrderResponseSummary>;
  refunds: SearchState<RefundResponse>;
  resellerRelationships: SearchState<OrgRelationshipsResponse>;
  customerRelationships: SearchState<OrgRelationshipsResponse>;
  users: SearchState<OrganizationUser>;
  customizations: SearchState<Customization>;
  grants: SearchState<GrantsResponse>;
}

type ConsoleSearchStatesKeys = keyof ConsoleSearchStates;

export interface ConsoleStateType {
  recentPurchaseOrders?: PartnerPurchaseOrderResponseSet;
  inventory?: OrganizationProductInventorySet;
  inventoryWithChannelPartners?: ChannelPartnerProductInventoryResponse | Object;
  dashboardSummary?: DashboardResponse | Object;
  recentShipments?: ShipmentResultSet;
  shipments?: ShipmentResultSet;
  incompleteShipments?: ShipmentResultSet;
  productResultSet?: ProductResultSet;
  countryResultSet?: Country[];
  organization?: Organization;
  channelPartnersResult?: ChannelPartnerResultSet;
  purchaseOrder?: PurchaseOrder;
  distributorPurchaseOrder?: PurchaseOrder;
  resellerPurchaseOrder?: PurchaseOrder;
  shipment?: ShipmentExactResponse;
  samlConfigurations?: Array<SAMLConfigurationResponse>;
  accountBalances?: AccountBalances;
  breadCrumbs?: BreadCrumbsParamsType;
  search: ConsoleSearchStates;
  inventoryAllocations: InventoryAllocationByProductIdResponse;
  orgOwners?: Array<string>;
  customizations?: CustomizationResultSet;
  featureToggles: Record<FeatureToggles, boolean>;
}

class SearchContext<TKey extends ConsoleSearchStatesKeys, TResponse> {
  constructor(
    public key: TKey,
    public search: (
      offset?: number,
      limit?: number,
      sortBy?: string,
      sortDirection?: SortDirection,
      search?: string | Array<string>,
      searchField?: string | Array<string>,
      advancedSearch?: boolean,
      options?: AxiosRequestConfig
    ) => AxiosPromise<TResponse>,
    public mapRows: (response: TResponse) => ConsoleSearchStates[TKey]['searchResponse']
    // eslint-disable-next-line no-empty-function
  ) {}
}

const getAbortController = constructGetAbortController<ConsoleSearchStates, ConsoleSearchStatesKeys>();

const getEmptyPo = (): PurchaseOrder => ({
  channelpartner_id: 1,
  organization_id: '',
  organization_name: '',
  order_date: new Date().toISOString(),
  purchaseorder_number: '',
  yubicoorder_number: '',
  yubico_subsidiary: 0,
  is_post_pay: true,
  is_tax_exempt: false,
  is_vat_valid: false,
  vat_number: '',
  is_override_shipping: false,
  override_shipping_charge: 0,
  override_shipping_key_limit: 0,
  key_contact: '',
  key_contact_email: '',
  closing_street_line1: '',
  closing_street_line2: '',
  closing_street_line3: '',
  closing_city: '',
  closing_region: '',
  closing_country_code_2: '',
  closing_postal_code: '',
  key_contact_phone_number: '',
  shipping_credit_purchased: 0,
  tax_credit_purchased: 0,
  vat_credit_purchased: 0,
  order_items: [],
  purchaseorder_history: [],
  channelpartner_name: '',
  channelpartner_netsuite_customer_id: '',
  reseller: null,
  distributor: null,
  allow_automated_shipments: true,
});

export const consoleInitialState: ConsoleStateType = {
  recentPurchaseOrders: {
    count: 0,
    total_count: 0,
    purchaseorders: [],
  },
  recentShipments: {
    count: 0,
    total_count: 0,
    shipments: [],
  },
  inventory: {},
  inventoryAllocations: {},
  inventoryWithChannelPartners: {},
  dashboardSummary: {},
  shipments: {
    count: 0,
    total_count: 0,
    shipments: [],
  },
  incompleteShipments: {
    count: 0,
    total_count: 0,
    shipments: [],
  },
  productResultSet: {
    count: 0,
    total_count: 0,
    products: [],
  },
  countryResultSet: [],
  channelPartnersResult: {},
  shipment: {},
  purchaseOrder: getEmptyPo(),
  distributorPurchaseOrder: getEmptyPo(),
  resellerPurchaseOrder: getEmptyPo(),
  samlConfigurations: [],
  accountBalances: {},
  breadCrumbs: {
    resellerCustomerDashboard: {},
  },
  featureToggles: {
    FENetSuiteSync: false,
    FEExcludeColumnsCSV: false,
    FECustomization: false,
    FECustomizationPhase2: false,
    FENotifications: false,
    EndUserPortal: false,
    Passwordless: false,
    PasswordlessCompliance: false,
    FEPermissions: false,
    FENetSuiteIDAddProductFields: false,
  },
  search: {
    shipments: {
      searchParams: {
        sortBy: 'shipment_request_date',
        ...getDefaultSearchParams(),
      },
    },
    incompleteShipments: {
      searchParams: {
        sortBy: 'shipment_request_date',
        ...getDefaultSearchParams(),
      },
    },
    purchaseOrders: {
      searchParams: {
        sortBy: 'order_date',
        ...getDefaultSearchParams(),
      },
    },
    users: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    customers: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    resellers: {
      searchParams: {
        sortBy: 'reseller_organization_name',
        ...getDefaultSearchParams(),
      },
    },
    distributorPurchaseOrders: {
      searchParams: {
        sortBy: 'purchaseorder_number',
        ...getDefaultSearchParams(),
      },
    },
    resellerPurchaseOrders: {
      searchParams: {
        sortBy: 'purchaseorder_number',
        ...getDefaultSearchParams(),
      },
    },
    refunds: {
      searchParams: {
        sortBy: 'shipment_id',

        ...getDefaultSearchParams(),
      },
    },
    resellerRelationships: {
      searchParams: {
        sortBy: 'reseller_organization_name',
        ...getDefaultSearchParams(),
      },
    },
    customerRelationships: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    customizations: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
    grants: {
      searchParams: {
        ...getDefaultSearchParams(),
      },
    },
  },
};

export const console = createModel<RootModel>()({
  state: { ...consoleInitialState } as ConsoleStateType,
  /* eslint-disable @typescript-eslint/no-unsafe-member-access  */
  selectors: (slice) => ({
    // creates a simple memoized selector
    showCustomizations() {
      return slice((state) => state.customizations?.total_count > 0);
    },
  }),
  /* eslint-enable @typescript-eslint/no-unsafe-member-access  */
  reducers: {
    accountBalances: (state, payload) => ({
      ...state,
      accountBalances: payload,
    }),
    recentPurchaseOrders: (state, payload) => ({
      ...state,
      recentPurchaseOrders: payload,
    }),
    recentShipments: (state, payload) => ({
      ...state,
      recentShipments: payload,
    }),
    incompleteShipments: (state, payload) => ({
      ...state,
      incompleteShipments: payload,
    }),
    inventory: (state, payload) => ({
      ...state,
      inventory: payload,
    }),
    inventoryAllocations: (state, payload) => ({
      ...state,
      inventoryAllocations: reduce(
        payload,
        (acc: InventoryAllocationByProductIdResponse, alloc: SubscriptionInventoryAllocationResponse) => {
          acc[alloc.product_id.toString()] = { ...alloc } as SubscriptionInventoryAllocationResponse;
          return acc;
        },
        {} as InventoryAllocationByProductIdResponse
      ),
    }),
    inventoryWithChannelPartners: (state, payload) => ({
      ...state,
      inventoryWithChannelPartners: payload,
    }),
    dashboardSummary: (state, payload: DashboardResponse) => ({
      ...state,
      dashboardSummary: payload,
    }),
    productResultSet: (state, payload) => ({
      ...state,
      productResultSet: payload,
    }),
    countryResultSet: (state, payload: CountryResultSet) => ({
      ...state,
      countryResultSet: payload.countries.sort((a: Country, b: Country) =>
        a.country_name > b.country_name ? 1 : -1
      ),
    }),
    channelPartnersResultSet: (state, payload) => ({
      ...state,
      channelPartnersResult: payload,
    }),
    organization: (state, payload) => ({
      ...state,
      organization: payload,
    }),
    shipment: (state, payload) => ({
      ...state,
      shipment: payload,
    }),
    shipments: (state, payload) => ({
      ...state,
      shipments: payload,
    }),
    purchaseOrder: (state, payload) => ({
      ...state,
      purchaseOrder: payload,
    }),
    resellerPurchaseOrder: (state, payload) => ({
      ...state,
      resellerPurchaseOrder: payload,
    }),
    resetState: () => ({
      ...consoleInitialState,
    }),
    /** Updates the search param for a specific search state.
     *  Resets the page if the search string/array changes.
     * */
    setSearchParams: (
      state,
      payload: {
        key: ConsoleSearchStatesKeys;
        searchParams: Partial<SearchParams>;
      }
    ) => {
      const getPage = () => {
        // If the search field changed, reset the page because response size may be different
        if (isNotEmpty(payload.searchParams.search)) {
          return DEFAULT_PAGE;
        }
        if (isNumber(payload.searchParams.page)) {
          return payload.searchParams.page;
        }

        return state.search[payload.key].searchParams.page;
      };

      return {
        ...state,
        search: {
          ...state.search,
          [payload.key]: {
            ...state.search[payload.key],
            searchParams: {
              ...state.search[payload.key].searchParams,
              ...payload.searchParams,
              page: getPage(),
            },
          },
        },
      };
    },
    setSearchDescription: (
      state,
      payload: {
        key: ConsoleSearchStatesKeys;
        searchDescription: SearchDescription;
      }
    ) => ({
      ...state,
      search: {
        ...state.search,
        [payload.key]: {
          ...state.search[payload.key],
          searchDescription: {
            ...(state.search[payload.key].searchDescription ?? {}),
            ...payload.searchDescription,
          },
        },
      },
    }),
    setSearchResponse: <TKey extends ConsoleSearchStatesKeys>(
      state: ConsoleStateType,
      payload: {
        key: TKey;
        searchResponse: ConsoleSearchStates[TKey]['searchResponse'];
      }
    ) => ({
      ...state,
      search: {
        ...state.search,
        [payload.key]: {
          ...state.search?.[payload.key],
          searchResponse: {
            ...(state.search[payload.key].searchResponse ?? {}),
            ...payload.searchResponse,
          },
        },
      },
    }),
    setEmptySearchResponse: (state, payload: ConsoleSearchStatesKeys) => ({
      ...state,
      search: {
        ...state.search,
        [payload]: {
          ...state.search?.[payload],
          searchResponse: {
            rows: [],
            totalCount: 0,
            responseCount: 0,
          },
        },
      },
    }),
    distributorPurchaseOrder: (state, payload) => ({
      ...state,
      distributorPurchaseOrder: payload,
    }),
    samlConfigurations: (state, payload: SAMLConfigurationResponse[]) => ({
      ...state,
      samlConfigurations: payload,
    }),
    updateSamlConfigurations: (state, payload: SAMLConfigurationResponse) => ({
      ...state,
      // TODO: fix me when we know config Id
      samlConfigurations: state.samlConfigurations.map((s: SAMLConfigurationResponse) =>
        s.sp_entity_id === payload.sp_entity_id ? payload : s
      ),
    }),
    resellerBreadCrumbsParams: (state, payload) => ({
      ...state,
      breadCrumbs: {
        ...state.breadCrumbs,
        resellerCustomerDashboard: { ...payload },
      },
    }),
    customBreadCrumbLabel: (state, payload) => ({
      ...state,
      breadCrumbs: {
        ...state.breadCrumbs,
        ...payload,
      },
    }),
    setOrgOwners: (state, payload: Array<string>) => ({
      ...state,
      orgOwners: payload,
    }),
    customizations: (state, payload: CustomizationResultSet) => ({
      ...state,
      customizations: payload,
    }),
    featureToggles: (state, payload: Record<FeatureToggles, boolean>) => ({
      ...state,
      featureToggles: payload,
    }),
  },
  effects: (dispatch) => ({
    async getRecentPurchaseOrders(count: number = 5) {
      const res = await api.PurchaseOrdersApi.getPurchaseOrdersOfOrganization(0, count, 'order_date', 'DESC');
      if (res.status === 200) {
        dispatch.console.recentPurchaseOrders(res.data);
      }
    },
    async getRecentShipments(count: number = 5) {
      const res = await api.ShipmentsApi.getShipmentsExact(0, count, 'shipment_request_date', 'DESC');
      if (res.status === 200) {
        dispatch.console.recentShipments(res.data);
      }
    },
    async getShipments() {
      const res = await api.ShipmentsApi.getShipmentsExact();
      if (res.status === 200) {
        dispatch.console.shipments(res.data);
      }
    },
    async getInventory() {
      const res = await api.InventoryApi.getInventoryOfOrganizationInContext();
      if (res.status === 200) {
        dispatch.console.inventory(res.data);
      }
    },
    async getInventoryAllocations() {
      const res = await api.InventoryAllocationApi.getInventoryAllocations();
      if (res.status === 200) {
        dispatch.console.inventoryAllocations(res.data?.allocations);
      }
    },
    async getProducts() {
      const res = await api.ProductsApi.getProducts();
      if (res.status === 200) {
        dispatch.console.productResultSet(res.data);
      }
    },
    async getCountries() {
      const res = await api.CountriesApi.listCountries();
      if (res.status === 200) {
        dispatch.console.countryResultSet(res.data);
      }
    },
    async getCurrentOrganization() {
      const res = await api.OrganizationsApi.getCurrentOrganization();
      if (res.status === 200) {
        dispatch.console.organization(res.data);
      }
    },
    async getShipmentById(ID: string) {
      const res = await api.ShipmentsApi.getShipmentExactById(ID);
      if (res.status === 200) {
        dispatch.console.shipment(res.data);
      }
    },
    async getPurchaseOrderById(ID: string) {
      const res = await api.PurchaseOrdersApi.getPurchaseOrderById(ID);
      if (res.status === 200) {
        dispatch.console.purchaseOrder(res.data);
      }
    },
    async getResellerPurchaseOrderById(ID: string) {
      const res = await api.PurchaseordersHiddenApi.getResellerPurchaseOrderById(ID);
      if (res.status === 200) {
        dispatch.console.resellerPurchaseOrder(res.data);
      }
    },
    async getChannelPartnersPerOrg() {
      const res = await api.ChannelpartnersApi.getAllChannelPartnersForOrg();
      if (res.status === 200) {
        dispatch.console.channelPartnersResultSet(res.data);
      }
    },
    async removeUserFromOrganization(userId: string) {
      const res = await api.UsersApi.deleteUserFromOrganization(userId);
      if (res.status === 200) {
        dispatch.console.getCurrentOrganization();
      }
    },
    async getDistributorPurchaseOrderById(ID: string) {
      const res = await api.PurchaseordersHiddenApi.getDistributorPurchaseOrderById(ID);
      if (res.status === 200) {
        dispatch.console.distributorPurchaseOrder(res.data);
      }
    },
    /** Search */
    async search<TKey extends ConsoleSearchStatesKeys, TResponse>(
      searchContext: SearchContext<TKey, TResponse>,
      state: RematchRootState<RootModel, Record<string, never>>
    ) {
      const { page, pageSize, sortBy, sortDirection, search, searchField } =
        state.console.search[searchContext.key].searchParams;

      const controller = getAbortController(searchContext.key);

      try {
        const response = await searchContext.search(
          Number.isInteger(page) && Number.isInteger(pageSize) ? page * pageSize : undefined,
          pageSize,
          sortBy,
          sortDirection,
          search,
          searchField,
          isNotEmpty(search) && Array.isArray(search),
          { signal: controller.signal }
        );

        dispatch.console.setSearchResponse({
          key: searchContext.key,
          searchResponse: searchContext.mapRows(response.data),
        });
      } catch {
        dispatch.console.setEmptySearchResponse(searchContext.key);
      }
    },
    async getSearchDescription<TKey extends ConsoleSearchStatesKeys>(payload: {
      key: TKey;
      request: () => AxiosPromise<SearchDescription>;
    }) {
      try {
        const response = await payload.request();

        dispatch.console.setSearchDescription({
          key: payload.key,
          searchDescription: response.data,
        });
      } catch {
        /* empty */
      }
    },
    async getCustomizationsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'customizations',
        request: api.CustomizationApi.listCustomizationSearchDescription,
      });
    },

    async searchUsers() {
      await dispatch.console.search(
        new SearchContext('users', api.UsersApi.listAllUsersInOrganization, (response) => ({
          rows: response.users,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getUsersSearchDescriptioon() {
      await dispatch.console.getSearchDescription({
        key: 'users',
        request: api.UsersApi.getUsersSearchDescription,
      });
    },
    async searchCustomizations() {
      await dispatch.console.search(
        new SearchContext('customizations', api.CustomizationApi.getCustomizations, (response) => ({
          rows: response.customizations || [],
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getShipmentsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'shipments',
        request: api.ShipmentsApi.listShipmenExactSearchDescription,
      });
    },
    async searchShipments() {
      await dispatch.console.search(
        new SearchContext('shipments', api.ShipmentsApi.getShipmentsExact, (response) => ({
          rows: response.shipments,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getIncompleteShipmentsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'incompleteShipments',
        request: api.ShipmentsApi.listShipmenExactSearchDescription,
      });
    },
    async searchIncompleteShipments() {
      await dispatch.console.search(
        new SearchContext(
          'incompleteShipments',
          (offset, limit, sortBy, sortDirection, search, searchField, advancedSearch, options) =>
            api.ShipmentsApi.getShipmentsExact(
              offset,
              limit,
              sortBy,
              sortDirection,
              [
                ShipmentStatus.ShipmentStateIncomplete,
                ShipmentStatus.ShipmentStateAddressInvalid,
                ShipmentStatus.ShipmentStateAddressFail,
              ].map((n) => `shipment_state_id::=::${n}`),
              undefined,
              true,
              options
            ),
          (response) => ({
            rows: response.shipments,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getPurchaseOrdersSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'purchaseOrders',
        request: api.PurchaseOrdersApi.listPurchaseOrdersSearchDescription,
      });
    },
    async searchPurchaseOrders() {
      await dispatch.console.search(
        new SearchContext(
          'purchaseOrders',
          api.PurchaseOrdersApi.getPurchaseOrdersOfOrganization,
          (response) => ({
            rows: response.purchaseorders,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getCustomersSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'customers',
        request: api.ResellerHiddenApi.listResellerViewSearchDescription,
      });
    },
    async searchCustomers() {
      await dispatch.console.search(
        new SearchContext('customers', api.ResellerHiddenApi.getResellerView, (response) => ({
          rows: response.reseller_view_items,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getResellersSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'resellers',
        request: api.DistributorHiddenApi.listDistributorViewSearchDescription,
      });
    },
    async searchResellers() {
      await dispatch.console.search(
        new SearchContext('resellers', api.DistributorHiddenApi.getDistributorView, (response) => ({
          rows: response.distributor_view_items,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getDistributorsPurchaseOrdersSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'distributorPurchaseOrders',
        request: api.PurchaseordersHiddenApi.listDistributorPurchaseOrdersSearchDescription,
      });
    },
    async searchDistributorPurchaseOrders() {
      await dispatch.console.search(
        new SearchContext(
          'distributorPurchaseOrders',
          api.PurchaseordersHiddenApi.getDistributorPurchaseOrders,
          (response) => ({
            rows: response.purchaseorders,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getResellerPurchaseOrdersSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'resellerPurchaseOrders',
        request: api.PurchaseordersHiddenApi.listResellerPurchaseOrdersSearchDescription,
      });
    },
    async searchResellerPurchaseOrders() {
      await dispatch.console.search(
        new SearchContext(
          'resellerPurchaseOrders',
          api.PurchaseordersHiddenApi.getResellerPurchaseOrders,
          (response) => ({
            rows: response.purchaseorders,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getRefundsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'refunds',
        request: api.RefundsApi.getShipmentRefundSearchDescription,
      });
    },
    async searchRefunds() {
      await dispatch.console.search(
        new SearchContext('refunds', api.RefundsApi.getAllShipmentRefunds, (response) => ({
          rows: response.refunds,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    async getResellerRelationshipsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'resellerRelationships',
        request: api.DistributorHiddenApi.listDistributorRelationshipsSearchDescription,
      });
    },
    async searchResellerRelationships() {
      await dispatch.console.search(
        new SearchContext(
          'resellerRelationships',
          api.DistributorHiddenApi.getRelationshipsOnDistributor,
          (response) => ({
            rows: response.relationships,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async getCustomerRelationshipsSearchDescription() {
      await dispatch.console.getSearchDescription({
        key: 'customerRelationships',
        request: api.ResellerHiddenApi.listResellerRelationshipsSearchDescription,
      });
    },
    async searchCustomerRelationships() {
      await dispatch.console.search(
        new SearchContext(
          'customerRelationships',
          api.ResellerHiddenApi.getRelationshipsOnReseller,
          (response) => ({
            rows: response.relationships,
            responseCount: response.count,
            totalCount: response.total_count,
          })
        )
      );
    },
    async searchGrants() {
      await dispatch.console.search(
        new SearchContext('grants', api.GrantsHiddenApi.getGrants, (response) => ({
          rows: response.grants,
          responseCount: response.count,
          totalCount: response.total_count,
        }))
      );
    },
    /** SSO SAML */
    async getAllSAMLConfigs() {
      const res = await api.SamlConfigurationApi.getAllSAMLConfigurations();
      if (res.status === 200) {
        dispatch.console.samlConfigurations(res.data);
      }
    },
    async getSAMLConfigurationById(id: string) {
      const res = await api.SamlConfigurationApi.getSAMLConfigurationById(id);
      if (res.status === 200) {
        dispatch.console.updateSamlConfigurations(res.data);
      }
    },
    async updateSAMLConfigurationById({
      id,
      config,
    }: {
      id: string;
      config: SAMLConfigurationUpdateRequest;
    }) {
      const res = await api.SamlConfigurationApi.updateSAMLConfigurationById(id, config);
      if (res.status === 200) {
        dispatch.console.getAllSAMLConfigs();
      }
    },
    async enableSAMLConfiguration({
      id,
      enableSSO,
    }: {
      id: string;
      enableSSO: SAMLConfigurationPatchRequest;
    }) {
      const res = await api.SamlConfigurationApi.enableSAMLConfiguration(id, enableSSO);
      if (res.status === 200) {
        dispatch.console.getAllSAMLConfigs();
        dispatch.auth.getUserInfo();
      }
    },
    async createSAMLConfiguration() {
      const res = await api.SamlConfigurationApi.createSAMLConfiguration();
      if (res.status === 201) {
        // Created the SSO config successfully, fetch again to trigger
        // a rerender with the new configuration.
        dispatch.console.getAllSAMLConfigs();
        dispatch.auth.getUserInfo();
      }
    },
    async getInventoryByChannelPartners() {
      const res = await api.InventoryHiddenApi.getInventoryOfOrganizationPerChannelPartner();
      if (res.status === 200) {
        dispatch.console.inventoryWithChannelPartners(res.data);
      }
    },
    async getDashboardSummary({ shippableOnly }: { shippableOnly?: boolean }) {
      const res = await api.DashboardHiddenApi.getOrganizationDashboard(shippableOnly);
      if (res.status === 200) {
        dispatch.console.dashboardSummary(res.data);
      }
    },
    async getAccountBalances() {
      const res = await api.AccountsApi.getAccountBalances();
      if (res.status === 200) {
        dispatch.console.accountBalances(res.data);
      }
    },
    async getCustomizations() {
      const res = await api.CustomizationApi.getCustomizations();
      if (res.status === 200) {
        dispatch.console.customizations(res.data);
      }
    },
    async getOrgOwners() {
      const res = await api.OrganizationApi.organizationGetNumOrgOwners();

      if (res.status === 200) {
        dispatch.console.setOrgOwners(res.data);
      }
    },
    resellerBreadCrumb(payload: { reseller: string; customer: string; href: string }) {
      dispatch.console.resellerBreadCrumbsParams(payload);
    },
    customBreadCrumb(payload: { [pathname: string]: { label: string } }) {
      dispatch.console.customBreadCrumbLabel(payload);
    },
    async getFeatureToggles() {
      const response = await api.FeatureTogglesHiddenApi.getDefaultToggles();

      dispatch.console.featureToggles(
        reduce(
          get(response, ['data', 'toggles']),
          (featureToggles, { name, enabled }) => ({
            ...featureToggles,
            [name]: enabled,
          }),
          {} as Record<FeatureToggles, boolean>
        )
      );
    },
  }),
});
